import { createAction } from '~reduxHelpers';
import {
  fetchNavigationItems,
  fetchProfileData,
  fetchNotifications,
  fetchInventory,
  logOutQuery,
  fetchOrganizationData,
  fetchMunicipalities,
  fetchOffices,
  fetchFormModels,
  getCostCenters,
} from '~services/api.helpers';

import * as cordovaUtils from '~utils/cordova.utils';
import {
  addUser,
  receiveUserOrganization,
  receiveNotifications,
} from '~features/user/ducks';
import { showServerErrorMsg } from './errorActions';

import { addToast } from '~features/toast/ducks';

import { renderRetryPage } from '../index';

import { config } from '../configuration';

const DELAY_THRESHOLD = 500;
const SPLASH_ANIMATION_DELAY = 2000;

// Actions ============================================================

export const setAppInitializing = createAction('SET_APP_INITIALIZING');
export const setAppInitialized = createAction('SET_APP_INITIALIZED');
export const receiveNavItems = createAction('RECEIVE_NAV_ITEMS');
export const receiveFormModels = createAction('RECEIVE_FORM_MODELS');
export const setWindowInfo = createAction('SET_WINDOW_INFO');
export const showHideProfile = createAction('SHOW_HIDE_PROFILE');
export const organizationSelected = createAction('ORGANIZATION_SELECTED');
export const organizationSet = createAction('ORGANIZATION_SET');
export const receiveOrganizations = createAction('RECEIVE_ORGANIZATIONS');
export const receiveCostCenters = createAction('RECEIVE_COST_CENTERS');
export const showValidation = createAction('VALIDATE_ORGANIZATION_SELECT');
export const saveInventoryCategories = createAction(
  'RECEIVE_INVENTORY_CATEGORIES'
);
export const setMunicipalities = createAction('SET_MUNICIPALITIES');
export const setOffices = createAction('SET_OFFICES');
export const receiveLabels = createAction('RECEIVE_LABELS');
export const setMobileFullscreen = createAction('SET_MOBILE_FULLSCREEN');

// Thunks
export function logOut() {
  return async () => {
    try {
      const logoutData = await logOutQuery();

      // TODO Not refactored to next condition as dev env does not provide proper logout url?
      if (window.cordova && config.isImprivata) {
        cordovaUtils.logout(logoutData.url);
      }

      if (logoutData && logoutData.url) {
        if (window.cordova) cordovaUtils.logout(logoutData.url);
        else window.location = logoutData.url;
      }
    } catch (e) {
      // dispatch(failLogout());
      console.error('Error in logout:', e);
    }
  };
}

export function fetchOrganizations() {
  return async dispatch => {
    try {
      const orgs = await fetchOrganizationData();
      dispatch(receiveOrganizations({ data: orgs, orgType: 'organizations' }));
    } catch (e) {
      console.error('Error in fetchOrganizations:', e);
    }
  };
}

export const fetchCostCenters = () => async dispatch => {
  try {
    const costCenters = await getCostCenters();
    dispatch(receiveCostCenters(costCenters));
  } catch (e) {
    dispatch(
      addToast({
        type: 'ERROR',
        message: 'Kustannuspaikkojen haku epäonnistui',
      })
    );
    console.error('Error in fetchCostCenters:', e);
  }
};

export function fetchMe() {
  return async dispatch => {
    try {
      const profile = await fetchProfileData();
      dispatch(addUser(profile));

      // TODO: why do we do this?
      if (profile.ordererOrganization == null) {
        dispatch(fetchOrganizations());
      }
    } catch (e) {
      console.error('Error in fetchMe:', e);
    }
  };
}

const handleDeepURL = (url, navigate) => {
  const path = url.slice(6); // Remove 'pake:/'
  navigate(path);
};

export function initializeApp(navigate) {
  return async dispatch => {
    try {
      // First load necessary data
      const start = new Date().getTime();

      const [
        orgs,
        userOrgs,
        municipalities,
        offices,
        profile,
        notifications,
        navItems,
        inventory,
        formModels,
      ] = await Promise.all([
        fetchOrganizationData(),
        fetchOrganizationData(true),
        fetchMunicipalities(),
        fetchOffices(),
        fetchProfileData(),
        fetchNotifications(),
        fetchNavigationItems(),
        fetchInventory(),
        fetchFormModels(),
      ]);

      dispatch(setMunicipalities(municipalities));
      dispatch(setOffices(offices));
      dispatch(receiveOrganizations({ data: orgs, orgType: 'organizations' }));
      dispatch(receiveUserOrganization(userOrgs));

      dispatch(receiveLabels(profile.labels));
      dispatch(addUser(profile));

      dispatch(receiveNotifications(notifications));
      dispatch(receiveNavItems(navItems));
      dispatch(saveInventoryCategories(inventory));
      dispatch(receiveFormModels(formModels));

      // NOTE: if fetching was fast we want to delay the splash screen animation
      const end = new Date().getTime();
      const delta = end - start;
      const timeout = delta < DELAY_THRESHOLD ? SPLASH_ANIMATION_DELAY : 0;

      // Support deep linking on Cordova
      if (window.cordova) {
        // Listen to new deep URLs
        window.addEventListener(
          'deepURL',
          e => {
            const deepURL = e.detail;
            handleDeepURL(deepURL, navigate);
          },
          false
        );

        // Use session storage when cold booting
        const deepURL = sessionStorage.getItem('deepURL');
        sessionStorage.clear();
        if (deepURL) {
          handleDeepURL(deepURL, navigate);
        }
      }

      // If we fetched user data and there's no organization,
      // initiate user onboarding
      if (!profile.ordererOrganization) {
        navigate('/onboarding');
      } else {
        // Otherwise we are good to go
        setTimeout(() => {
          dispatch(setAppInitializing(false));
          dispatch(setAppInitialized(true));
        }, timeout);
      }
    } catch (e) {
      console.log('App initialization failed:', e);
      if (e && e.message) console.log(e.message);
      dispatch(showServerErrorMsg(true));
      // TODO Retry after a while?
      if (window.cordova)
        renderRetryPage(`Cordova: ${e && e.message ? e.message : '<no info>'}`);
    }
  };
}
